import { VsfContext } from '~/composables/context';
import { ComposableFunctionArgs } from '~/composables/types';
import { Logger } from '~/helpers/logger';
import { Cart } from '~/modules/GraphQL/types';

import { CartContextChangedError } from '../cartCoordinator';

export const loadCartCommand = {
  execute: async (context: VsfContext, params: ComposableFunctionArgs<{
    realCart?: boolean;
  }>) => {
    const apiState = context.$magento.config.state;
    Logger.debug('[Magento Storefront]: loadCartCommand Loading Cart');
    const customerToken = apiState.getCustomerToken();
    const virtual = !params.realCart;
    const locale = apiState.getLocale();
    const owner = {
      customer: customerToken || '',
      store: apiState.getStore?.() || '',
      locale: locale || '',
      currency: apiState.getCurrency?.() || '',
    };
    let expectedCartId = apiState.getCartId() || '';
    const assertContext = () => {
      if ((apiState.getCustomerToken() || '') !== owner.customer
        || (apiState.getStore?.() || '') !== owner.store
        || (apiState.getLocale() || '') !== owner.locale
        || (apiState.getCurrency?.() || '') !== owner.currency
        || (apiState.getCartId() || '') !== expectedCartId) {
        throw new CartContextChangedError();
      }
    };
    const adoptCartId = (id: string) => {
      assertContext();
      apiState.setCartId(id || undefined);
      expectedCartId = id;
      assertContext();
    };
    const createVirtualCart = () => (null as Cart);

    const createRealCart = async (): Promise<string> => {
      Logger.debug('[Magento Storefront]: useCart.load.createNewCart');

      assertContext();
      if (expectedCartId) throw new CartContextChangedError();
      const { data, errors } = await context.$magento.api.createEmptyCart();
      assertContext();
      if (errors?.length || typeof data?.createEmptyCart !== 'string' || !data.createEmptyCart) {
        throw new Error(errors?.[0]?.message || 'Could not create a cart.');
      }
      adoptCartId(data.createEmptyCart);
      return data.createEmptyCart;
    };

    const getCartData = async (id: string) => {
      assertContext();
      if (id !== expectedCartId) throw new CartContextChangedError();

      const cartQueryString = locale === 'cn' ? 'cart-query-toal-cn' : 'cart-query-toal';
      console.info('*** [load cart -> cartQueryString:', cartQueryString);
      const { data, errors } = await context.$magento.api.cart(id, {
        cart: cartQueryString,
      });
      assertContext();

      if (!data?.cart || errors?.length) {
        throw errors?.[0] ?? new Error('Can not confirm your cart data');
      }
      if (data.cart.id !== id || !Array.isArray(data.cart.items)) {
        throw new Error('Can not confirm your cart data');
      }
      data.cart.items = data.cart.items.filter(Boolean);
      return data.cart as unknown as Cart;
    };

    const getCart = async (virtualCart: boolean, cartId?: string) => {
      assertContext();
      if (!cartId) {
        console.warn('*** not find cartID ***');
        if (virtualCart) {
          return createVirtualCart();
        }

        // eslint-disable-next-line no-param-reassign
        cartId = await createRealCart();
      }

      return getCartData(cartId);
    };

    // Try to load cart for existing customer, clean customer token if not possible
    if (customerToken) {
      console.log('*** Has customerToken, start to load Cart ***');
      try {
        // 先获取ID，有则获取详情；没有则先取id再获取详情;
        const currentcartId = apiState.getCartId();
        if (!currentcartId) {
          console.warn('*** No currentcartId ***');
          const { data, errors } = await context.$magento.api.customerCart();
          assertContext();
          if (!data?.customerCart || errors?.length) {
            throw errors?.[0] ?? new Error('Can not confirm your cart data');
          }
          if (typeof data.customerCart.id !== 'string' || !data.customerCart.id) {
            throw new Error('Can not confirm your cart data');
          }
          adoptCartId(data.customerCart.id);
          // 根据cartID获取详情
        }
        return await getCart(virtual, apiState.getCartId()) as unknown as Cart;
      } catch (e) {
        Logger.error('loadCartCommand/customer', e);
        throw e;
      }
    }

    try {
      // If it's not existing customer check if cart id is set and try to load it
      const cartId = apiState.getCartId();
      return await getCart(virtual, cartId);
    } catch (error) {
      // A transient transport failure must not discard a valid cart or create
      // a replacement. Only a confirmed unavailable cart may reset its ID.
      assertContext();
      if (!expectedCartId || error instanceof CartContextChangedError
        || !/could not find a cart|cart (?:isn't|is not|is not currently) active|cart is inactive/i.test(error?.message || '')) {
        throw error;
      }
      adoptCartId('');
      return await getCart(virtual);
    }
  },
};
